import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import {memo, useCallback, useMemo} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'

import {getHrefWithQueryParams} from '../../../../routes'
import {getProjectsAndOverviewReady} from '../../../../selectors/projectTrees'
import type {BuildTypeId, ProjectId, ProjectOrBuildTypeNode, TestId} from '../../../../types'
import {getProjectBuildTypeFilter, stringifyId} from '../../../../types'
import {ProjectBuildTypeSelect} from '../../../common/ProjectBuildTypeSelect/ProjectBuildTypeSelect'
import {testsApi} from '../../../packages/Tests/Tests.rest'
import {hierarchyToBuildTypesList} from '../../../packages/Tests/Tests.selectors'

import styles from './ProjectsSelect.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  className?: string
  testId: TestId | null | undefined
  projectId: ProjectId | null | undefined
  buildTypeId: BuildTypeId | null | undefined
}

function ProjectsSelect({className, projectId, buildTypeId, testId}: Props) {
  const navigate = useNavigate()
  const location = useLocation()

  const activeItem = getProjectBuildTypeFilter({buildTypeId, projectId})

  const readyProjectsAndOverview = useAppSelector(state => getProjectsAndOverviewReady(state))

  const onChangeQueryParams = useCallback(
    (newParams: Record<string, string | null | undefined>) =>
      navigate(getHrefWithQueryParams(location, null, () => newParams)),
    [location, navigate],
  )

  const {hierarchy, ready} = testsApi.endpoints.getBuildTypeHierarchy.useQuery(
    {projectId, testNameId: testId},
    {selectFromResult: ({data, isSuccess}) => ({hierarchy: data, ready: isSuccess})},
  )
  const testBuildTypeList = useMemo(() => hierarchyToBuildTypesList(hierarchy), [hierarchy])

  const onSelect = useCallback(
    (item: ProjectOrBuildTypeNode | null | undefined) => {
      switch (item?.nodeType) {
        case 'bt':
          onChangeQueryParams({
            currentBuildTypeId: stringifyId(item.id),
            currentProjectId: stringifyId(item.parentId),
          })
          break

        case 'project':
          onChangeQueryParams({
            currentBuildTypeId: null,
            currentProjectId: stringifyId(item.id),
          })
          break

        default:
      }
    },
    [onChangeQueryParams],
  )
  return (
    <div className={classNames(className, styles.wrapper)}>
      {ready && readyProjectsAndOverview ? (
        <ProjectBuildTypeSelect
          className={styles.select}
          popupClassName={styles.selectPopup}
          selected={activeItem}
          onSelect={onSelect}
          projectsSelectable
          expandAll
          includedBuildTypes={testBuildTypeList}
        />
      ) : (
        <LoaderInline>{'Loading projects...'}</LoaderInline>
      )}
    </div>
  )
}

export default memo<Props>(ProjectsSelect)
